// client/customer/src/pages/Reserve/by-date/ConfirmReservation.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext } from '../../../contexts/useReserveContext';
import { fetchServiceDurationRanges } from '../../../utils/reservation/by-Date/fetchServiceDurationRanges';
import { postCreateReservationByDate } from '../../../utils/reservation/by-Date/postConfirmReservation';

// ✅ 共通UI
import ReserveStepHeader from '../ReserveStepHeader';
import ReserveSelectionSummary from '../ReserveSelectionSummary';
import ReserveStepFooter from '../ReserveStepFooter';
import { useI18n } from '../../../i18n';

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);
const formatDurationRange = (min: number, max: number) =>
  min === max ? `${min} min` : `${min}min~${max}min`;

const addMinutesToHHmm = (start: string, minutes: number): string => {
  const [h, m] = start.split(':').map(Number);
  const total = h * 60 + m + minutes;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const ConfirmReservationByDate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const {
    storeId,
    storeName,
    selectedDate,
    selectedStaffId,
    selectedStaffName,
    selectedServices,
    selectedStartTime,
    isFreeReservation,
    setSelectedServices,
    setSelectedDate,
    setSelectedStartTime,
    setSelectedStaffId,
    setSelectedStaffName,
    setIsFreeReservation,
  } = useReserveContext();

  const [submitting, setSubmitting] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [durationRanges, setDurationRanges] = useState<Record<number, { min: number; max: number }>>({});

  const customerName = localStorage.getItem('customerName') || t('account.guest');
  const customerId = Number(localStorage.getItem('customerId'));
  const serviceIds = useMemo(
    () => selectedServices.map((service) => Number(service.serviceId)).filter((id) => Number.isFinite(id) && id > 0),
    [selectedServices],
  );

  // ✅ ガード（TS2345回避：navigateしたら return;）
  useEffect(() => {
    if (hasConfirmed) return;

    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-date/store');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate('/reserve/by-date/date');
      return;
    }
    if (!selectedStartTime) {
      toast.info(t('time.selectWarning'));
      navigate('/reserve/by-date/time');
      return;
    }
    if (selectedServices.length === 0) {
      toast.info(t('date.selectServiceFirst'));
      navigate('/reserve/by-date/service');
      return;
    }
    if (!selectedStaffId && !isFreeReservation) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-date/staff');
      return;
    }
  }, [
    storeId,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    selectedServices.length,
    isFreeReservation,
    hasConfirmed,
    navigate,
    t,
  ]);

  useEffect(() => {
    const loadRanges = async () => {
      if (!isFreeReservation || !storeId || serviceIds.length === 0) {
        setDurationRanges({});
        return;
      }

      try {
        const rows = await fetchServiceDurationRanges(storeId, serviceIds);
        const nextRanges: Record<number, { min: number; max: number }> = {};
        rows.forEach((row) => {
          nextRanges[row.service_id] = {
            min: Number(row.min_duration_minutes) || 0,
            max: Number(row.max_duration_minutes) || 0,
          };
        });
        setDurationRanges(nextRanges);
      } catch (error) {
        console.error(error);
        setDurationRanges({});
      }
    };

    void loadRanges();
  }, [isFreeReservation, storeId, serviceIds]);

  const exactTotalMinutes = useMemo(
    () => selectedServices.reduce((sum, s: any) => sum + (Number(s.duration_minutes) || 0), 0),
    [selectedServices]
  );

  const totalDurationRange = useMemo(() => {
    if (!isFreeReservation) {
      return { min: exactTotalMinutes, max: exactTotalMinutes };
    }

    return selectedServices.reduce(
      (acc, service: any) => {
        const range = durationRanges[Number(service.serviceId)];
        const fallback = Number(service.duration_minutes) || 0;
        acc.min += range?.min ?? fallback;
        acc.max += range?.max ?? fallback;
        return acc;
      },
      { min: 0, max: 0 },
    );
  }, [durationRanges, exactTotalMinutes, isFreeReservation, selectedServices]);

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s: any) => sum + (Number(s.price) || 0), 0),
    [selectedServices]
  );

  const endTime = useMemo(() => {
    if (!selectedStartTime || isFreeReservation) return null;
    return addMinutesToHHmm(selectedStartTime, exactTotalMinutes);
  }, [selectedStartTime, exactTotalMinutes, isFreeReservation]);

  const handleConfirm = async () => {
    if (submitting) return;

    if (!storeId || !selectedDate || !selectedStartTime) {
      toast.error(t('service.incomplete'));
      return;
    }
    if (!selectedStaffId && !isFreeReservation) {
      toast.error(t('date.selectStaffFirst'));
      return;
    }
    if (selectedServices.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      navigate('/reserve/by-date/service');
      return;
    }

    setSubmitting(true);
    try {
      await postCreateReservationByDate({
        storeId,
        customerId: Number.isFinite(customerId) && customerId > 0 ? customerId : null,
        customerName: customerName || t('account.guest'),
        date: selectedDate,
        staffId: isFreeReservation ? null : selectedStaffId,
        isFree: isFreeReservation,
        startTime: selectedStartTime,
        services: selectedServices.map((s: any) => ({ service_id: s.serviceId })),
      });

      toast.success(t('confirm.success'));

      setHasConfirmed(true);
      navigate('/reserve', { replace: true });

      // reset
      setSelectedServices([]);
      setSelectedDate(null);
      setSelectedStartTime(null);
      setSelectedStaffId(null);
      setSelectedStaffName(null);
      setIsFreeReservation(false);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || t('confirm.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('confirm.title')}
        mode="confirm"
        onBack={() => navigate('/reserve/by-date/service')}
      />

      <section className="space-y-3 rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
        <div className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            {t('confirm.summary')}
          </h2>
          <ReserveSelectionSummary
            customerName={customerName}
            storeName={storeName}
            date={selectedDate}
            startTime={selectedStartTime}
            endTime={isFreeReservation ? null : endTime ?? null}
            staffName={selectedStaffName}
            variant="light"
            onSelectStore={() => navigate('/reserve/by-date/store')}
            onSelectStaff={() => navigate('/reserve/by-date/staff?edit=1')}
            onSelectDate={() => navigate('/reserve/by-date/date?edit=1')}
            onSelectTime={() => navigate('/reserve/by-date/time?edit=1')}
          />
        </div>

        <div className="h-px bg-slate-200" />

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('confirm.selectedServices')}
            </h2>
            <button
              type="button"
              onClick={() => navigate('/reserve/by-date/service?edit=1')}
              className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50"
            >
              {t('confirm.change')}
            </button>
          </div>

          <div className="space-y-2">
            {selectedServices.map((sv: any) => {
              const categoryName = sv.categoryName || '';
              const title = categoryName ? `${categoryName} • ${sv.serviceName}` : sv.serviceName;
              const range = durationRanges[Number(sv.serviceId)];
              const fallbackDuration = Number(sv.duration_minutes) || 0;
              const durationLabel = isFreeReservation
                ? formatDurationRange(range?.min ?? fallbackDuration, range?.max ?? fallbackDuration)
                : `${sv.duration_minutes} min`;

              return (
                <div
                  key={sv.serviceId}
                  className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-slate-800">{title}</span>
                    <span className="text-[11px] text-slate-500">{durationLabel}</span>
                    {isFreeReservation ? (
                      <span className="text-[11px] text-slate-400">
                        {t('confirm.freeDurationNote', {
                          duration: formatDurationRange(range?.min ?? fallbackDuration, range?.max ?? fallbackDuration),
                        })}
                      </span>
                    ) : null}
                  </div>
                  <span className="text-sm font-semibold text-slate-800">{money(sv.price)}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div className="text-slate-600">
            {t('confirm.total')}{' '}
            <span className="font-semibold text-slate-800">
              {formatDurationRange(totalDurationRange.min, totalDurationRange.max)}
            </span>
          </div>
          <div className="text-slate-600">
            {t('confirm.amount')} <span className="font-semibold text-slate-800">{money(totalPrice)}</span>
          </div>
        </div>

        {isFreeReservation ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
            {t('confirm.freeDurationNote', {
              duration: formatDurationRange(totalDurationRange.min, totalDurationRange.max),
            })}
          </div>
        ) : null}
      </section>

      <ReserveStepFooter
        summary={
          isFreeReservation
            ? t('confirm.startSummary', {
                time: selectedStartTime ?? '',
                duration: formatDurationRange(totalDurationRange.min, totalDurationRange.max),
                amount: money(totalPrice),
              })
            : t('confirm.timeSummary', {
                start: selectedStartTime ?? '',
                end: endTime ?? '--',
                minutes: exactTotalMinutes,
                amount: money(totalPrice),
              })
        }
        primaryLabel={submitting ? t('confirm.confirming') : t('confirm.confirmReservation')}
        onPrimary={handleConfirm}
        primaryDisabled={submitting}
      />
    </div>
  );
};

export default ConfirmReservationByDate;
